import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import { AppShell, LoginPrompt, NotFoundPanel, StartupPanel } from './components.js';
import { useAppState } from './AppState.js';
import { DashboardPage, MatchesPage, RosterPage, StandingsPage } from './pagesDashboardMatches.js';
import { CreateLeaguePage } from './pagesCreateLeague.js';
import { AdminCoursesPage } from './pagesAdminCourses.js';
import { AdminHomePage, AdminMembersPage, AdminSeasonPlayersPage, AdminSeasonsPage } from './pagesAdminCore.js';
import { AdminInvitesPage } from './pagesAdminInvites.js';
import { AdminMatchDayPage } from './pagesAdminMatchDay.js';
import { AdminMatchDaysPage } from './pagesAdminMatchDays.js';
import { AdminScoreEntryPage } from './pagesAdminScoreEntry.js';
import { GuestPortalPage } from './pagesGuestPortal.js';
import { MatchDetailPage } from './pagesMatchDetail.js';
import { ProfilePage } from './pagesProfile.js';
import { HomePage, JoinInvitePage } from './pagesPublic.js';
import { MatchDaysPage } from './pagesMatchDays.js';
import { MatchDayPage } from './pagesMatchDay.js';
import { useLeagueRole } from './hooks.js';

export function App() {
  return (
    <BrowserRouter>
      <AppShellRouter />
    </BrowserRouter>
  );
}

function AppShellRouter() {
  const { bootstrapStatus, bootstrapError } = useAppState();

  if (bootstrapStatus === 'bootstrapping') {
    return <StartupPanel title="Loading App" message="Checking session and loading your leagues." />;
  }

  if (bootstrapStatus === 'error') {
    return <StartupPanel title="Unable to Start App" message={bootstrapError || 'Verify Clerk and API connectivity, then refresh.'} />;
  }

  return (
    <AppShell>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/join/:token" element={<JoinInvitePage />} />
        <Route path="/leagues/:leagueId/guest" element={<GuestPortalPage />} />
        <Route
          path="/leagues"
          element={
            <RequireAuthenticated>
              <DashboardPage />
            </RequireAuthenticated>
          }
        />
        <Route
          path="/leagues/new"
          element={
            <RequireAuthenticated>
              <CreateLeaguePage />
            </RequireAuthenticated>
          }
        />
        <Route
          path="/leagues/:leagueId/standings"
          element={
            <RequireLeagueMember>
              <StandingsPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/matches"
          element={
            <RequireLeagueMember>
              <MatchesPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/roster"
          element={
            <RequireLeagueMember>
              <RosterPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/matches/:matchId"
          element={
            <RequireLeagueMember>
              <MatchDetailPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/profile"
          element={
            <RequireLeagueMember>
              <ProfilePage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/players/:playerId"
          element={
            <RequireLeagueMember>
              <ProfilePage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/match-days"
          element={
            <RequireLeagueMember>
              <MatchDaysPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/match-days/:matchDayId"
          element={
            <RequireLeagueMember>
              <MatchDayPage />
            </RequireLeagueMember>
          }
        />
        <Route
          path="/leagues/:leagueId/admin"
          element={
            <RequireLeagueAdmin>
              <AdminHomePage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/members"
          element={
            <RequireLeagueAdmin>
              <AdminMembersPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/seasons"
          element={
            <RequireLeagueAdmin>
              <AdminSeasonsPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/seasons/:seasonId/players"
          element={
            <RequireLeagueAdmin>
              <AdminSeasonPlayersPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/courses"
          element={
            <RequireLeagueAdmin>
              <AdminCoursesPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/score-entry"
          element={
            <RequireLeagueAdmin>
              <AdminScoreEntryPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/match-days"
          element={
            <RequireLeagueAdmin>
              <AdminMatchDaysPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/match-days/:matchDayId"
          element={
            <RequireLeagueAdmin>
              <AdminMatchDayPage />
            </RequireLeagueAdmin>
          }
        />
        <Route
          path="/leagues/:leagueId/admin/invites"
          element={
            <RequireLeagueAdmin>
              <AdminInvitesPage />
            </RequireLeagueAdmin>
          }
        />
        <Route path="*" element={<NotFoundPanel />} />
      </Routes>
    </AppShell>
  );
}

function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      window.requestAnimationFrame(() => {
        document.querySelector(location.hash)?.scrollIntoView({ block: 'start' });
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.hash, location.pathname]);

  return null;
}

function RequireAuthenticated({ children }: { children: React.ReactNode }) {
  const { session } = useAppState();
  if (!session) {
    return <LoginPrompt message="Please log in to continue." />;
  }
  return <>{children}</>;
}

function RequireLeagueMember({ children }: { children: React.ReactNode }) {
  const { session, leagues, setActiveLeague, setBanner } = useAppState();
  const { leagueId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!leagueId) {
      return;
    }
    setActiveLeague(leagueId);
    if (session && leagues.length > 0 && leagues.every((league) => league.id !== leagueId)) {
      setBanner('error', 'You are not a member of that league.');
      navigate('/leagues', { replace: true });
    }
  }, [leagueId, leagues, navigate, session, setActiveLeague, setBanner]);

  if (!session) {
    return <LoginPrompt message="Please log in to continue." />;
  }

  if (!leagueId) {
    return <Navigate replace to="/leagues" />;
  }

  if (leagues.length > 0 && leagues.every((league) => league.id !== leagueId)) {
    return null;
  }

  return <>{children}</>;
}

function RequireLeagueAdmin({ children }: { children: React.ReactNode }) {
  const { leagueId } = useParams();
  const { setBanner } = useAppState();
  const navigate = useNavigate();
  const { role, isLoading } = useLeagueRole(leagueId ?? '');

  useEffect(() => {
    if (!leagueId || isLoading) {
      return;
    }
    if (role !== 'admin') {
      setBanner('error', 'Admin access is required for that route.');
      navigate('/leagues', { replace: true });
    }
  }, [isLoading, leagueId, navigate, role, setBanner]);

  return (
    <RequireLeagueMember>
      {isLoading ? <StartupPanel title="Checking Access" message="Validating admin permissions." /> : role === 'admin' ? children : null}
    </RequireLeagueMember>
  );
}
